import { useTranslation } from 'next-i18next'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import useCurriculumGradeLabels, { GradeRef } from '~/curriculum/hooks/useCurriculumGradeLabels'
import { TopicId } from '~/dashboard/utils/learningPath.utils'
import CertificateItem from '~/statistics-v2/components/CertificateItem'
import { toGradeRef } from '~/statistics-v2/helper/statistics.helper'
import { MasteryCertificatesProgressProps } from '~/statistics-v2/types/statistics.types'

const COLLAPSED_ROWS = 1
const EXPANDED_ROWS = 4

export default function MasteryCertificatesProgress({
  certificates,
  isAllGrades,
}: MasteryCertificatesProgressProps): React.ReactElement | null {
  const { t } = useTranslation('statisticsV2')
  const [displayAll, setDisplayAll] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState<number | undefined>(undefined)
  const [expandedHeight, setExpandedHeight] = useState<number | undefined>(undefined)
  const [canExpand, setCanExpand] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  const earnedCount = useMemo(() => certificates.filter((c) => c.isCompleted).length, [certificates])
  // All grades lists only earned certificates, by grade; a single grade lists all of them, earned first
  const items = useMemo(() => {
    const withGrade = certificates.map((certificate) => ({ certificate, gradeRef: toGradeRef(certificate.gradeLevel) }))
    const earned = withGrade.filter(({ certificate }) => certificate.isCompleted)
    const levelOf = (item: (typeof withGrade)[number]): number => item.gradeRef?.gradeLevel ?? Infinity
    // `earned` is a fresh array, so sort in place: toSorted needs iOS 16+
    return isAllGrades
      ? earned.sort((a, b) => (levelOf(a) === levelOf(b) ? 0 : levelOf(a) - levelOf(b)))
      : [...earned, ...withGrade.filter(({ certificate }) => !certificate.isCompleted)]
  }, [certificates, isAllGrades])

  const gradeRefs = useMemo(
    () => (isAllGrades ? items.map(({ gradeRef }) => gradeRef).filter((ref): ref is GradeRef => !!ref) : []),
    [isAllGrades, items],
  )
  const gradeLabelOf = useCurriculumGradeLabels(gradeRefs)

  // Rows vary in height (titles wrap), so cap at the measured top of the first hidden row
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list) {
      return undefined
    }
    const measure = (): void => {
      const listTop = list.getBoundingClientRect().top - list.scrollTop
      const rowTops = [
        ...new Set(Array.from(list.children).map((item) => Math.round(item.getBoundingClientRect().top - listTop))),
      ]
      const rowGap = parseFloat(getComputedStyle(list).rowGap) || 0
      const heightOfRows = (rows: number): number =>
        rowTops[rows] === undefined ? list.scrollHeight : rowTops[rows] - rowGap
      setCollapsedHeight(heightOfRows(COLLAPSED_ROWS))
      setExpandedHeight(heightOfRows(EXPANDED_ROWS))
      setCanExpand(rowTops.length > COLLAPSED_ROWS)
    }
    measure()
    // The list's own height animates on toggle; only a width change or an item resize moves the rows
    let lastWidth = list.clientWidth
    const observer = new ResizeObserver((entries) => {
      const isListHeightOnly = entries.every((entry) => entry.target === list) && list.clientWidth === lastWidth
      lastWidth = list.clientWidth
      if (!isListHeightOnly) {
        measure()
      }
    })
    observer.observe(list)
    Array.from(list.children).forEach((item) => observer.observe(item))
    return () => observer.disconnect()
  }, [items])

  const toggleDisplayAll = (): void => {
    if (displayAll) {
      listRef.current?.scrollTo({ top: 0 })
    }
    setDisplayAll(!displayAll)
  }

  if (items.length === 0) {
    return null
  }

  return (
    <div className="flex min-w-full gap-2 justify-center items-center">
      <div className="self-start flex flex-col justify-between p-6 w-full border rounded-6 border-white/5 bg-purple-plum overflow-hidden">
        <div className="flex justify-between items-start">
          <div className="flex flex-col">
            <h2 className="text-white text-md font-bold flex items-center gap-2">🏆 {t('masteryCertificates')}</h2>
            <span className="text-blue-pastel text-xs">
              {isAllGrades
                ? `${earnedCount} ${t('collected')}`
                : `${earnedCount} / ${certificates.length} ${t('collected')}`}
            </span>
          </div>
          {canExpand && (
            <button
              className="text-white text-xs font-medium hover:text-blue-pastel transition-colors"
              onClick={toggleDisplayAll}
            >
              {displayAll ? t('seeLess') : t('seeAll')}
            </button>
          )}
        </div>
        <div
          className={`flex flex-wrap items-start mt-6 pt-2 gap-x-4 lg:gap-x-12 gap-y-9 justify-center transition-all duration-1000 ease-in-out ${
            displayAll
              ? 'overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-purple scrollbar-track-transparent'
              : 'overflow-hidden'
          }`}
          ref={listRef}
          style={{ maxHeight: displayAll ? expandedHeight : collapsedHeight }}
        >
          {items.map(({ certificate, gradeRef }) => (
            <CertificateItem
              badgeSrcKey={`curriculum/certificate/mastery-certificate-${
                certificate.topicSlug ?? TopicId.NUMBERS
              }_v4.png`}
              date={certificate.completionDate}
              isLocked={!certificate.isCompleted}
              key={`${certificate.gradeLevel}-${certificate.topicId}`}
              subtitle={isAllGrades ? gradeLabelOf(gradeRef) : undefined}
              title={certificate.topicName}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
